import { fetchAuthSession } from 'aws-amplify/auth'

export const API_BASE_URL = (() => {
  const base = import.meta.env.VITE_WALLET_API_URL ?? ''
  if (!base) return ''
  return base.endsWith('/') ? base.slice(0, -1) : base
})()

export interface User {
  id: string
  walletAddress: string
  email?: string
  isActive: boolean
}

export interface ApiResponse<T> {
  user: T
  ts?: string
}

/**
 * 認証トークンを取得する関数（IDトークンを使用）
 */
async function getAuthToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken?.toString()
    console.log('ID Token:', idToken)
    return idToken || null
  } catch (error) {
    console.error('Failed to get ID token:', error)
    return null
  }
}

/**
 * API呼び出し用の共通関数
 */
async function apiCall<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = await getAuthToken()

  const config: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    ...options,
  }

  if (!API_BASE_URL) {
    throw new Error('VITE_WALLET_API_URL is not configured')
  }

  const url = `${API_BASE_URL}${endpoint}`
  console.log('[apiCall] Fetching:', url)

  const response = await fetch(url, config)

  console.log('[apiCall] Response status:', response.status)

  if (!response.ok) {
    // レスポンスボディからエラーメッセージを取得
    try {
      const errorData = await response.json()
      const errorMessage =
        errorData.error || errorData.message || `${response.status} ${response.statusText}`
      throw new Error(errorMessage)
    } catch (e) {
      if (e instanceof Error && !e.message.includes(String(response.status))) {
        throw e
      }
      throw new Error(`API call failed: ${response.status} ${response.statusText}`)
    }
  }

  return response.json()
}

/**
 * GET /user/me - 現在のユーザー情報を取得
 */
export async function getCurrentUser(): Promise<ApiResponse<User>> {
  console.log('getCurrentUser called')
  return apiCall<ApiResponse<User>>('/user/me')
}

export type MarketRange = '1D' | '1W' | '1M' | '3M' | '1Y'

export type PriceSeriesPoint = {
  time: number
  value: number
}

export interface PriceSeriesResponse {
  pair: string
  latestPrice: { value: number; timestamp: number } | null
  change24h: { absolute: number | null; percent: number | null }
  series: Partial<Record<MarketRange, PriceSeriesPoint[]>>
}

export async function getPriceSeries(
  pair: string,
  signal?: AbortSignal
): Promise<PriceSeriesResponse> {
  const params = new URLSearchParams({ pair }).toString()
  return apiCall<PriceSeriesResponse>(`/prices?${params}`, { signal })
}

export interface LockupStats {
  totalLocked: number | string
  totalUnlocked: number | string
  currentlyLocked: number | string
  activeLockupsCount: number | string
  unlockedLockupsCount: number | string
  nextUnlockDate?: string | null
}

export interface LockupStatsResponse {
  stats: LockupStats
}

export async function getLockupStats(signal?: AbortSignal): Promise<LockupStatsResponse> {
  return apiCall<LockupStatsResponse>('/user/lockup/stats', { signal })
}

export type HistoryTypeFilter = 'increase' | 'decrease' | 'all'

export interface HistoryAsset {
  id: string
  name: string
  symbol: string
  decimals: number
}

export interface HistoryCategory {
  id: string
  name: string
  description?: string | null
}

export interface HistoryOrder {
  id: string
  quoteId: string
  status: OrderStatus
  arxAmount: string
  ethAmount: string
  arxPriceJpy: string
  ethPriceJpy: string
  ethTxHash?: string | null
  arxTxHash?: string | null
  createdAt: string
  updatedAt: string
}

export interface WalletHistory {
  id: string
  amount: number
  description?: string | null
  createdAt: string
  asset: HistoryAsset
  category: HistoryCategory
  onchainHistory?: {
    id: string
    txHash: string
    traceId?: string | null
    fromAddress: string
    toAddress: string
    value: string
    blockNumber: number
    blockTimestamp: string
    createdAt: string
  } | null
  order?: HistoryOrder | null
}

export interface UserHistoriesResponse {
  histories: WalletHistory[]
}

export interface GetUserHistoriesParams {
  limit?: number
  offset?: number
  type?: HistoryTypeFilter
  categoryId?: string
  assetId?: string
  signal?: AbortSignal
}

export interface FetchUserOnchainHistoriesResponse extends UserHistoriesResponse {
  latestBlockNumber: number
  assetResults: Array<
    | {
        symbol: string
        status: 'synced'
        fetchedCount: number
      }
    | {
        symbol: string
        status: 'skipped'
        reason: string
      }
    | {
        symbol: string
        status: 'error'
        error: string
      }
  >
}

export async function getUserHistories(
  params: GetUserHistoriesParams = {}
): Promise<UserHistoriesResponse> {
  const { limit, offset, type, categoryId, assetId, signal } = params
  const searchParams = new URLSearchParams()
  if (limit != null) searchParams.set('limit', String(limit))
  if (offset != null) searchParams.set('offset', String(offset))
  if (type) searchParams.set('type', type)
  if (categoryId) searchParams.set('categoryId', categoryId)
  if (assetId) searchParams.set('assetId', assetId)
  const query = searchParams.toString()
  const endpoint = query ? `/user/histories?${query}` : '/user/histories'
  return apiCall<UserHistoriesResponse>(endpoint, { signal })
}

/**
 * POST /user/histories/fetch - オンチェーン履歴を同期して取得
 */
export async function fetchUserOnchainHistories(): Promise<FetchUserOnchainHistoriesResponse> {
  return apiCall<FetchUserOnchainHistoriesResponse>('/user/histories/fetch', {
    method: 'POST',
  })
}

/**
 * GET /users/{walletAddress} - ウォレットアドレスでユーザー情報を取得
 */
export async function getUserByWalletAddress(walletAddress: string): Promise<{ user: User }> {
  return apiCall<{ user: User }>(`/users/${walletAddress}`)
}

/**
 * PUT /user/email - ユーザーのメールアドレスを登録・更新
 */
export async function updateUserEmail(email: string): Promise<{ user: User }> {
  return apiCall<{ user: User }>('/user/email', {
    method: 'PUT',
    body: JSON.stringify({ email }),
  })
}

// ==========================================
// OTC購入API
// ==========================================

export interface OtcConfig {
  quoteExpirationSeconds: number
  maxArxPurchaseAmount: string
  minArxPurchaseAmount: string
  feeRatePercent: number
}

export interface OtcConfigResponse {
  success: boolean
  config: OtcConfig
}

export interface Quote {
  quoteId: string
  arxAmount: string
  ethAmount: string
  arxPriceEth: string
  arxPriceJpy: string
  ethPriceJpy: string
  arxTransferGasEth: string
  totalJpy: string
  expiresAt: string
  maxArxAmount: string
  minArxAmount: string
  nonce: string // リプレイ攻撃防止用
  hmac: string
}

export interface QuoteResponse {
  success: boolean
  quote: Quote
  error?: string
}

export interface ConfirmQuoteResponse {
  success: boolean
  order: {
    quoteId: string
    arxAmount: string
    ethAmount: string
    otcWalletAddress: string
    expiresAt: string
  }
  error?: string
}

/**
 * GET /otc/config - OTC設定取得
 */
export async function getOtcConfig(signal?: AbortSignal): Promise<OtcConfigResponse> {
  return apiCall<OtcConfigResponse>('/otc/config', { signal })
}

/**
 * GET /otc/quote - 見積もり取得（レート表示用）
 *
 * 見積もりは常に1 ARXで作成され、レート表示用として使用されます。
 * 実際の購入量はconfirmQuote時にarxAmountで指定します。
 */
export async function getQuote(signal?: AbortSignal): Promise<QuoteResponse> {
  return apiCall<QuoteResponse>('/otc/quote', { signal })
}

/**
 * GET /otc/quote/:quoteId - 既存見積もり取得
 */
export async function getQuoteById(quoteId: string, signal?: AbortSignal): Promise<QuoteResponse> {
  return apiCall<QuoteResponse>(`/otc/quote/${encodeURIComponent(quoteId)}`, { signal })
}

/**
 * POST /otc/quote/:quoteId/confirm - 見積もり確定（注文作成）
 *
 * @param quoteId - 見積もりID
 * @param hmac - HMAC署名
 * @param arxAmount - 実際に購入するARX量（省略時はQuoteの量を使用）
 * @param signal - AbortSignal
 */
export async function confirmQuote(
  quoteId: string,
  hmac: string,
  arxAmount?: string,
  signal?: AbortSignal
): Promise<ConfirmQuoteResponse> {
  return apiCall<ConfirmQuoteResponse>(`/otc/quote/${encodeURIComponent(quoteId)}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ hmac, ...(arxAmount && { arxAmount }) }),
    signal,
  })
}

/**
 * 購入開始レスポンス
 */
export interface PurchaseStartResponse {
  success: boolean
  historyId?: string
  error?: string
}

/**
 * POST /otc/purchase/start - 購入開始（履歴をPENDINGで作成、Orderにリンク）
 */
export async function startPurchase(
  arxAmount: string,
  quoteId: string,
  signal?: AbortSignal
): Promise<PurchaseStartResponse> {
  return apiCall<PurchaseStartResponse>('/otc/purchase/start', {
    method: 'POST',
    body: JSON.stringify({ arxAmount, quoteId }),
    signal,
  })
}

/**
 * 購入完了レスポンス
 */
export interface PurchaseCompleteResponse {
  success: boolean
  history?: {
    id: string
    amount: string
    status: string
    createdAt: string
  }
  error?: string
}

/**
 * POST /otc/purchase/:historyId/complete - 購入完了（ステータス更新）
 */
export async function completePurchase(
  historyId: string,
  txHash: string,
  signal?: AbortSignal
): Promise<PurchaseCompleteResponse> {
  return apiCall<PurchaseCompleteResponse>(
    `/otc/purchase/${encodeURIComponent(historyId)}/complete`,
    {
      method: 'POST',
      body: JSON.stringify({ txHash }),
      signal,
    }
  )
}

/**
 * 購入失敗レスポンス
 */
export interface PurchaseFailResponse {
  success: boolean
  history?: {
    id: string
    status: string
  }
  error?: string
}

/**
 * POST /otc/purchase/:historyId/fail - 購入失敗（ステータス更新）
 */
export async function failPurchase(
  historyId: string,
  reason?: string,
  signal?: AbortSignal
): Promise<PurchaseFailResponse> {
  return apiCall<PurchaseFailResponse>(`/otc/purchase/${encodeURIComponent(historyId)}/fail`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    signal,
  })
}

// ==========================================
// OTC Order Status API
// ==========================================

/**
 * Order ステータス
 */
export type OrderStatus =
  | 'PENDING'
  | 'ETH_SENT'
  | 'ETH_RECEIVED'
  | 'ARX_SENT'
  | 'COMPLETED'
  | 'EXPIRED'
  | 'FAILED'

/**
 * ETH送金報告レスポンス
 */
export interface ReportEthSentResponse {
  success: boolean
  order?: {
    id: string
    quoteId: string
    status: OrderStatus
    ethTxHash?: string | null
  }
  error?: string
  // 自動復旧中（トランザクション検証がタイムアウトした場合）
  pendingRecovery?: boolean
  message?: string
}

/**
 * POST /otc/order/:quoteId/eth-sent - ETH送金報告
 */
export async function reportEthSent(
  quoteId: string,
  txHash: string,
  signal?: AbortSignal
): Promise<ReportEthSentResponse> {
  return apiCall<ReportEthSentResponse>(`/otc/order/${encodeURIComponent(quoteId)}/eth-sent`, {
    method: 'POST',
    body: JSON.stringify({ txHash }),
    signal,
  })
}

/**
 * Orderステータス取得レスポンス
 */
export interface GetOrderStatusResponse {
  success: boolean
  order?: {
    id: string
    quoteId: string
    status: OrderStatus
    ethTxHash: string | null
    arxTxHash: string | null
    arxAmount: string
    ethAmount: string
  }
  error?: string
}

/**
 * GET /otc/order/:quoteId/status - Orderステータス取得
 */
export async function getOrderStatus(
  quoteId: string,
  signal?: AbortSignal
): Promise<GetOrderStatusResponse> {
  return apiCall<GetOrderStatusResponse>(`/otc/order/${encodeURIComponent(quoteId)}/status`, {
    signal,
  })
}
